'use client';

import { useMemo } from 'react';
import { MEETING_ASIDE_KEY, SUCCESS_STEP_KEY } from './stepKeys';
import { useStepNavigation } from './useStepNavigation';
import { useStepValidation, type StepValidationState, type StepStateMap } from './useStepValidation';
type UseStepsArgs = Partial<{
  initialStep: string;
}>;
function neutralStepState(stepKey: string): StepValidationState {
  return {
    stepKey,
    stepName: stepKey,
    isSubmitted: false,
    isValid: true,
    isInvalid: false,
    errorCount: 0,
    isSkipped: false,
    isComplete: false,
    isPristine: true
  };
}
export type ComposedStepRow = StepValidationState & {
  isActive: boolean;
  isNavigable: boolean;
};
export function useSteps({
  initialStep
}: UseStepsArgs = {}) {
  const navigation = useStepNavigation({
    initialStep
  });
  const {
    stepStateMap,
    activeStepIsValid
  } = useStepValidation({
    activeStep: navigation.activeStep,
    visitedStepKeys: navigation.visitedStepKeys,
    submittedStepKeys: navigation.submittedStepKeys
  });
  const {
    activeStep,
    canVisitStep,
    visibleSteps
  } = navigation;
  const steps = useMemo((): ComposedStepRow[] => {
    // The success pseudo-step is URL-driven only; it must not appear as a row
    // in the sidebar (ContentsList).
    return visibleSteps.filter(stepKey => stepKey !== SUCCESS_STEP_KEY && stepKey !== MEETING_ASIDE_KEY).map(stepKey => {
      const state = stepStateMap[stepKey] ?? neutralStepState(stepKey);
      return {
        ...state,
        isActive: stepKey === activeStep,
        isNavigable: canVisitStep(stepKey)
      };
    });
  }, [visibleSteps, activeStep, canVisitStep, stepStateMap]);
  return {
    ...navigation,
    steps,
    stepStateMap,
    activeStepIsValid
  };
}
export type { StepStateMap, StepValidationState };